/**
 * Auto-update guard for long-lived tabs and home-screen PWAs (especially on mobile, which rarely gets
 * a manual hard-reload). The main bundle is content-hashed, so a new deploy changes its filename in
 * index.html. When the tab is re-shown after being backgrounded, we fetch the (no-store) shell and, if
 * it now references a NEWER main bundle than the one running, reload to pick it up. Refocus is the safe
 * moment: the user just returned to the app and expects it fresh, and we never reload mid-session.
 */

// The running main bundle's URL (…/assets/index-<hash>.js) — Vite resolves import.meta.url to it.
const RUNNING = import.meta.url;

export function installAutoUpdate(): void {
  if (typeof document === 'undefined' || typeof fetch === 'undefined' || !RUNNING) return;
  let busy = false;
  let wasHidden = false;

  const check = async () => {
    if (busy || document.visibilityState !== 'visible') return;
    busy = true;
    try {
      const res = await fetch('/', { cache: 'no-store' });
      if (!res.ok) return;
      const html = await res.text();
      const m = html.match(/<script[^>]+src="(\/assets\/index-[A-Za-z0-9_-]+\.js)"/);
      if (m && !RUNNING.endsWith(m[1])) window.location.reload();
    } catch {
      /* offline / blocked — try again next refocus */
    } finally {
      busy = false;
    }
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      wasHidden = true;
      return;
    }
    if (wasHidden) {
      wasHidden = false;
      void check();
    }
  });
}
